import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import moment from 'moment';
import { ErrorToast } from '../components/common/Toasts/Toasts';
import {
  setUserRegisterToLS,
  setUserToLS,
  setSignInUserToLS,
  LOCAL_STORAGE_USER_REGISTER_NODE,
  LOCAL_STORAGE_USER_SIGNIN_NODE,
  setTokensToLS
} from '../helpers/localstorage-helper';
import {
  DASHBOARD,
  EMAIL_ADDRESS,
  LICENCE_EXPIRING,
  MY_HUBS,
  PASSWORD,
  PROFILE_DETAILS,
  REFERRAL,
  SECURITY_CODE,
  TERMS_AND_CONDITIONS
} from '../routes/routes';
import {
  RegistrationStatus,
  UserInfoType,
  SignInType,
  SignInGoogleType,
  SignInCompleteType,
  SignInResponseType,
  authInitialStateType,
  UserTicketsModel,
  UserTypes,
  VirtualOfficeLicenceModel,
  User
} from '../types/loginTypes';
import {
  addEmailAndVerificationCode,
  addEmailToNewTeamMember
} from '../views/EmailAddress/EmailAddress.actions';
import { signUpPassword } from '../views/Password/Password.actions';
import {
  updateProfileDetails,
  updateSocialProfileDetails
} from '../views/ProfileDetails/ProfileDetails.actions';
import {
  getReferrerValidation,
  getReferrerUsernameValidation,
  socialReferrer
} from '../views/Referral/Referral.actions';
import { termsAndConditionsSocialSign } from '../views/TermsConditions/TermsConditions.actions';
import {
  resetPassword,
  sendResetPasswordEmail
} from '../views/ForgottenPassword/ForgottenPassword.actions';
import { RootState } from '../app/store';
import {
  decodeJwt,
  isPartnerHelper,
  licenceExpiresIn30Days
} from '../helpers/helperFunctions';
import { setMavieMigrationModalChecked } from '../views/MigrationTermsPrivacy/MigrationTermsPrivacy.actions';
import { UserType } from '../views/Dashboard/Dashboard';

export const signIn = createAsyncThunk(
  'auth/signIn',
  async (
    {
      email,
      password,
      result,
      history,
      finup,
      finupTest,
      simplicity,
      simplicityRedirectUri
    }: SignInType,
    { rejectWithValue, dispatch }
  ) => {
    try {
      const {
        lot_number: lotNumber,
        captcha_output: captchaOutput,
        pass_token: passToken,
        gen_time: genTime
      } = result;

      const { data } = await axios.post(`/authorization/signin`, {
        email,
        password,
        lotNumber,
        captchaOutput,
        passToken,
        genTime
      });

      const {
        is2FARequired,
        isEmailCodeVerificationRequired,
        signInToken,
        user
      } = data.data;

      if (isEmailCodeVerificationRequired || is2FARequired) {
        setSignInUserToLS(data.data);
        if (simplicity === 'true') {
          history.push(
            `${SECURITY_CODE}?simplicity=true&redirect_uri=${simplicityRedirectUri}`
          );
        } else {
          history.push(SECURITY_CODE);
        }
      } else {
        dispatch(
          signInComplete({
            signInToken,
            emailCode: null,
            twoFAcode: null,
            is2FARequired,
            history,
            finup,
            finupTest,
            simplicity,
            simplicityRedirectUri
          })
        );
      }

      return {
        is2FARequired,
        isEmailCodeVerificationRequired,
        signInToken,
        user
      } as SignInResponseType;
    } catch (error: any) {
      if (error?.response?.data?.data?.user) {
        const user = error.response.data.data?.user;

        if (user?.registrationStatus === RegistrationStatus.EmailVerified) {
          history.push(PASSWORD);
        } else if (
          user?.registrationStatus === RegistrationStatus.EmailVerification
        ) {
          ErrorToast('Please confirm verification mail');
        } else {
          history.push(PROFILE_DETAILS);

          const newUser = {
            user
          };

          localStorage.setItem('prizerRegister', JSON.stringify(newUser));
        }
      } else if (error?.response?.data?.error) {
        ErrorToast(error?.response?.data.error);
      }
      return rejectWithValue(error?.response?.data?.data?.user);
    }
  }
);

const registrationStatusesRedirect = (
  registrationStatus: number,
  history: any
) => {
  switch (registrationStatus) {
    case RegistrationStatus.SocialNetworkCreation:
      return history.push(REFERRAL);

    case RegistrationStatus.SocialNetworkReferrerAdded:
      return history.push(PROFILE_DETAILS);

    case RegistrationStatus.SocialNetworkTermsAndConditionsAccepted:
      return history.push(PROFILE_DETAILS);

    default:
      break;
  }
};

export const signInGoogle = createAsyncThunk(
  'auth/signInGoogle',
  async (
    {
      token,
      referrerId,
      captchaResult,
      history,
      finup,
      finupTest,
      simplicity,
      simplicityRedirectUri
    }: SignInGoogleType,
    { rejectWithValue, dispatch }
  ) => {
    try {
      const {
        lot_number: lotNumber,
        captcha_output: captchaOutput,
        pass_token: passToken,
        gen_time: genTime
      } = captchaResult;

      let { data } = await axios.post(`/authorization/signup/google`, {
        idToken: token,
        lotNumber,
        captchaOutput,
        passToken,
        genTime
      });

      const {
        signInToken,
        is2FARequired,
        isEmailCodeVerificationRequired,
        user
      } = data.data;

      //registration
      if (
        user?.registrationStatus === RegistrationStatus.SocialNetworkCreation
      ) {
        if (!referrerId) {
          history.push(REFERRAL);

          setUserRegisterToLS({ referrerId, user, token });

          return { user, token };
        } else {
          dispatch(
            socialReferrer({
              referrerId,
              token,
              history
            })
          );

          setUserRegisterToLS({ referrerId, user, token });

          return user;
        }
      } else {
        //login

        if (isEmailCodeVerificationRequired || is2FARequired) {
          if (simplicity === 'true') {
            history.push(
              `${SECURITY_CODE}?simplicity=true&redirect_uri=${simplicityRedirectUri}`
            );
          } else {
            history.push(SECURITY_CODE);
          }
          setSignInUserToLS(data.data);
          return data.data;
        } else {
          dispatch(
            signInComplete({
              signInToken,
              emailCode: null,
              twoFAcode: null,
              is2FARequired,
              history,
              finup,
              finupTest,
              simplicity,
              simplicityRedirectUri
            })
          );
        }
      }
    } catch (error: any) {
      const { user } = error?.response?.data?.error as UserInfoType;

      if (user) {
        registrationStatusesRedirect(user?.registrationStatus, history);

        setUserRegisterToLS({ referrerId: user?.referrerId, user, token });

        return { user, token };
      } else {
        ErrorToast(error?.response?.data?.error);
        return rejectWithValue(error.response.data.error);
      }
    }
  }
);

export const signInComplete = createAsyncThunk(
  'auth/signInComplete',
  async (
    {
      signInToken,
      emailCode,
      twoFAcode,
      is2FARequired,
      history,
      finup,
      finupTest,
      simplicity,
      simplicityRedirectUri
    }: SignInCompleteType,
    { rejectWithValue }
  ) => {
    try {
      const response = await axios.post(`/authorization/signin/complete`, {
        signInToken,
        emailCode: emailCode ? emailCode : null,
        twoFAcode: is2FARequired ? twoFAcode : null
      });

      const userInfo = {
        ...response.data.data,
        userType: decodeJwt(response.data.data.token.accessToken)
      } as UserInfoType;

      setUserToLS(userInfo);
      setTokensToLS(userInfo.token);

      const expiresIn30Days = licenceExpiresIn30Days(
        userInfo?.user?.currentVirtualOfficeLicence?.dateEnd || ''
      );

      const isPartner = isPartnerHelper(
        userInfo?.user?.currentVirtualOfficeLicence?.dateEnd || ''
      );

      if (expiresIn30Days && isPartner) {
        history.push(LICENCE_EXPIRING);
      } else {
        const redirectTo = () => {
          if (simplicity === 'true') {
            return `/simplicity?redirect_uri=${simplicityRedirectUri}&userId=${userInfo.user.id}`;
          } else if (finup === 'true') {
            return '/finup';
          } else if (finupTest === 'true') {
            return '/finup-test';
          } else if (userInfo.userType === UserTypes.Regular) {
            return DASHBOARD;
          } else if (userInfo.userType === UserTypes.Restricted) {
            return MY_HUBS;
          }
        };

        history.push(redirectTo());
      }

      localStorage.removeItem(LOCAL_STORAGE_USER_REGISTER_NODE);
      localStorage.removeItem(LOCAL_STORAGE_USER_SIGNIN_NODE);

      return userInfo;
    } catch (error: any) {
      ErrorToast(error.response.data.error);
      return rejectWithValue(error.response.data.error);
    }
  }
);

export const signInUserType = createAsyncThunk(
  'auth/signInUserType',
  async ({ userType }: { userType: number }, { getState, rejectWithValue }) => {
    try {
      await axios.post(`/authorization/signin/user-type`, {
        userType
      });

      const state = getState() as RootState;

      const userInfo = {
        ...state.auth.userInfo,
        user: {
          ...state.auth.userInfo.user,
          currentVirtualOfficeLicence: {
            ...state.auth.userInfo.user.currentVirtualOfficeLicence,
            dateEnd: moment().toISOString()
          }
        }
      } as UserInfoType;

      setUserToLS(userInfo);

      return userInfo;
    } catch (error: any) {
      ErrorToast(error.response.data.error);
      return rejectWithValue(error.response.data.error);
    }
  }
);

interface ContinueToAffiliateTermsType {
  referrerId: number;
  history: any;
}

export const continueToAffiliateTerms = createAsyncThunk(
  'auth/continueToAffiliateTerms',
  ({ referrerId, history }: ContinueToAffiliateTermsType) => {
    history.push(EMAIL_ADDRESS);

    // setUserRegisterToLS({ referrerId });

    return referrerId;
  }
);

export const getUserTickets = createAsyncThunk(
  'auth/getTickets',
  async (_, { getState }) => {
    const { data } = await axios.get(`/user/tickets`);

    const userTickets = data.data.userTickets;

    const state = getState() as RootState;

    const user = {
      ...state.auth.userInfo,
      user: {
        ...state.auth.userInfo.user,
        userTickets: userTickets
      }
    };

    setUserToLS(user);

    return userTickets as UserTicketsModel;
  }
);

export const getCurrentLicense = createAsyncThunk(
  'auth/getCurrentLicence',
  async (_, { getState }) => {
    const { data } = await axios.get(`/user/info`);

    const user = data.data.user;
    const referrer = data.data.referrer;
    const isCanadaRestrictedUser = data.data.isCanadaRestrictedUser;
    const isJapanRestrictedUser = data.data.isJapanRestrictedUser;
    const canBuyBigWhale = data.data.canBuyBigWhale;
    const canBuyTravelPackages = data.data.canBuyTravelPackages;
    const canBuyStakingPackages = data.data.canBuyStakingPackages;
    const canBuyFlipMePackages = data.data.canBuyFlipMePackages;

    const state = getState() as RootState;

    const userForLs = {
      ...state.auth.userInfo,
      user,
      referrer,
      isCanadaRestrictedUser,
      isJapanRestrictedUser,
      canBuyBigWhale,
      canBuyTravelPackages,
      canBuyStakingPackages,
      canBuyFlipMePackages
    };

    setUserToLS(userForLs);

    return userForLs as UserInfoType;
  }
);

export const logoutRemoveState = createAsyncThunk(
  'auth/logout',
  async function (_, { dispatch }) {
    dispatch({ type: 'logout/LOGOUT' });
  }
);

const initialState = {
  userInfo: {},
  loading: false,
  register: {
    user: null
  }
} as authInitialStateType;

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    storeRegisterInfo(state, { payload }) {
      state.register = payload;
    },
    storeApprovalDate(state, { payload }) {
      state.register.approvalDate = payload;
    },
    storeAuthUser(state, { payload }) {
      state.register.user = payload;
    },
    changeUserInfo(state, { payload }) {
      state.userInfo = payload;
    },
    changeUserEmail(state, { payload }) {
      state.userInfo.user.email = payload;
    },
    emptyUnseenNotificationCount(state) {
      state.userInfo.unseenNotificationCount = 0;
    },
    changeUserTickets(state, { payload }) {
      state.userInfo.user.userTickets = payload;
    },
    changeAvailableRewardPoints(state, { payload }) {
      state.userInfo.user.userTickets.availableRewardPoints = payload;
    },

    changeAvailableShoppingPoints(state, { payload }) {
      state.userInfo.user.userTickets.shoppingPoints = payload;
    },
    changeAvailableLoyaltyPoints(state, { payload }) {
      state.userInfo.user.userTickets.loyaltyPoints = payload;
    },
    changeMyLifePoints(state, { payload }) {
      state.userInfo.user.userTickets.myLifePoints = payload;
    },
    updateVirtualOfficeLicence(state, { payload }) {
      state.userInfo.user.currentVirtualOfficeLicence = payload;
    }
    // setMavieMigrationModalChecked(state) {
    //   state.userInfo.isMavieMigrationModalChecked = true;
    // }
  },

  extraReducers: (builder) => {
    builder.addCase(signIn.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(signIn.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.signIn = payload;
    });
    builder.addCase(signIn.rejected, (state, { payload }) => {
      state.loading = false;
      state.register.user = payload;
    });

    builder.addCase(signInGoogle.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(signInGoogle.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.register = payload;
      state.signIn = payload;
    });
    builder.addCase(signInGoogle.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(signInComplete.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(signInComplete.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.userInfo = payload;
      state.register = {};
    });
    builder.addCase(signInComplete.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(getReferrerValidation.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getReferrerValidation.fulfilled, (state, { payload }) => {
      state.loading = false;
      if (payload?.referrerId) {
        state.register.referrerId = payload.referrerId;
        state.register.user = payload.user;
        state.register.referrer = payload.referrer;
      }
    });
    builder.addCase(getReferrerValidation.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(getReferrerUsernameValidation.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(
      getReferrerUsernameValidation.fulfilled,
      (state, { payload }) => {
        state.loading = false;
        if (payload?.referrerId) {
          state.register.referrerId = payload.referrerId;
          state.register.user = payload.user;
          state.register.referrer = payload.referrer;
        }
      }
    );
    builder.addCase(getReferrerUsernameValidation.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(socialReferrer.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(socialReferrer.fulfilled, (state, { payload }) => {
      state.loading = false;
      // state.register = payload;
    });
    builder.addCase(socialReferrer.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(termsAndConditionsSocialSign.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(
      termsAndConditionsSocialSign.fulfilled,
      (state, { payload }) => {
        state.loading = false;
        state.register = payload;
      }
    );
    builder.addCase(termsAndConditionsSocialSign.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(addEmailToNewTeamMember.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(addEmailToNewTeamMember.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.register = payload;
    });
    builder.addCase(addEmailToNewTeamMember.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(addEmailAndVerificationCode.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(
      addEmailAndVerificationCode.fulfilled,
      (state, { payload }) => {
        state.loading = false;
        state.register = payload;
      }
    );
    builder.addCase(addEmailAndVerificationCode.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(signUpPassword.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(signUpPassword.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.register = payload;
    });
    builder.addCase(signUpPassword.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(updateProfileDetails.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(updateProfileDetails.fulfilled, (state) => {
      state.loading = false;
      state.register = {};
    });
    builder.addCase(updateProfileDetails.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(updateSocialProfileDetails.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(updateSocialProfileDetails.fulfilled, (state) => {
      state.loading = false;
      state.register = {};
    });
    builder.addCase(updateSocialProfileDetails.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(sendResetPasswordEmail.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(sendResetPasswordEmail.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(sendResetPasswordEmail.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(resetPassword.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(resetPassword.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(resetPassword.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(getUserTickets.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getUserTickets.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.userInfo.user.userTickets = payload;
    });
    builder.addCase(getUserTickets.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(getCurrentLicense.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getCurrentLicense.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.userInfo = payload;
    });
    builder.addCase(getCurrentLicense.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(setMavieMigrationModalChecked.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(setMavieMigrationModalChecked.fulfilled, (state) => {
      state.loading = false;
      state.userInfo.isFirstLoginPopupChecked = true;
    });
    builder.addCase(setMavieMigrationModalChecked.rejected, (state) => {
      state.loading = false;
    });

    //CONTINUE TO REFFERER TERMS AND CONDITIONS
    builder.addCase(
      continueToAffiliateTerms.fulfilled,
      (state, { payload }) => {
        state.register = {
          ...state.register,
          referrerId: payload
        };
      }
    );

    //USER TYPE
    builder.addCase(signInUserType.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(signInUserType.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.userInfo = payload;
    });
    builder.addCase(signInUserType.rejected, (state) => {
      state.loading = false;
    });
  }
});

export const {
  storeRegisterInfo,
  storeApprovalDate,
  storeAuthUser,
  changeUserInfo,
  changeUserEmail,
  emptyUnseenNotificationCount,
  changeUserTickets,
  changeAvailableRewardPoints,
  changeAvailableShoppingPoints,
  changeMyLifePoints,
  updateVirtualOfficeLicence,
  changeAvailableLoyaltyPoints
} = authSlice.actions;

export default authSlice.reducer;
